import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { AnnouncementType, usePublishedAnnouncementsQuery } from 'api/queries/announcements'
import Button from 'components/button'
import { Spinner } from 'components/spinner/spinner'
import withIdleTimer from 'lib/HOC/withIdleTimer'
import { markAnnouncementsRead } from 'lib/announcement-read-state'
import { pathsObj } from 'lib/constants/paths'
import Main from 'layout/main'

const categoryLabels: Record<AnnouncementType, string> = {
  [AnnouncementType.NEWS]: 'NEWS',
  [AnnouncementType.FEATURE]: "WHAT'S NEW",
  [AnnouncementType.OFFER]: 'OFFER',
}

const formatPublishedDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
}).format(new Date(value))

const Updates = () => {
  const navigate = useNavigate()
  const { data, loading, error } = usePublishedAnnouncementsQuery()
  const announcements = useMemo(() => data?.publishedAnnouncements ?? [], [data?.publishedAnnouncements])

  useEffect(() => {
    if (announcements.length) markAnnouncementsRead(announcements)
  }, [announcements])

  return (
    <Main className="nextround updatesPage">
      <section className="updatesPage__intro">
        <h1>Latest news &amp; updates</h1>
        <Button color="light" small customClickEvent={() => navigate(pathsObj.main.home)}>
          Back to home
        </Button>
      </section>

      {loading && !data && <Spinner className="updatesPage__loading" />}
      {error && (
        <div className="updatesPage__state" role="alert">
          <h3>Updates are temporarily unavailable</h3>
          <p>Please try again in a moment.</p>
        </div>
      )}
      {!loading && !error && announcements.length === 0 && (
        <div className="updatesPage__state">
          <h3>No updates yet</h3>
          <p>Check back soon for the latest NextRound news.</p>
        </div>
      )}

      <div className="updatesPage__feed" aria-live="polite">
        {announcements.map((announcement, index) => (
          <article className={`updateCard updateCard--${announcement.type.toLowerCase()}`} key={announcement.id}>
            <div className="updateCard__rail" />
            <div className="updateCard__content">
              <div className="updateCard__meta">
                <span>{categoryLabels[announcement.type]}</span>
                {index === 0 && <strong>LATEST</strong>}
                <time dateTime={announcement.publishedAt}>{formatPublishedDate(announcement.publishedAt)}</time>
              </div>
              <h2>{announcement.title}</h2>
              <p>{announcement.content}</p>
              {announcement.ctaLabel && announcement.ctaUrl && (
                <a href={announcement.ctaUrl} rel="noopener noreferrer" target="_blank">
                  {announcement.ctaLabel}
                  <span aria-hidden="true"> {'\u2192'}</span>
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </Main>
  )
}

export default withIdleTimer(Updates)
